import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  Grid,
  Group,
  ThemeIcon,
  Table,
  Loader,
  Button,
} from "@mantine/core";
import {
  IconReportAnalytics,
  IconCalendar,
  IconDownload,
  IconRefresh,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { usePerformRequest } from "../../../lib/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import { DatePickerInput } from "@mantine/dates";

interface FeeCollectionSummary {
  totalFeesCollected: number;
  totalFeeTransactions: number;
  activeFees: number;
  averageFeeAmount: number;
}

interface FeeByType {
  feeName: string;
  feeType: string;
  feeValue: number;
  totalCollected: number;
  transactionCount: number;
}

interface FeeByDay {
  _id: string;
  count: number;
  totalAmount: number;
}

interface FeeCollectionResponse {
  summary: FeeCollectionSummary;
  feesByType: FeeByType[];
  feesByDay: FeeByDay[];
}

export default function FeeCollectionReport() {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateRange, setDateRange] = useState<{ startDate?: number; endDate?: number }>({});

  const {
    data: reportData,
    isLoading,
    reloadData,
  } = usePerformRequest<{ data: FeeCollectionResponse }>({
    method: Endpoints.GetFeeCollectionReport(
      dateRange.startDate,
      dateRange.endDate
    ).method,
    route: Endpoints.GetFeeCollectionReport(
      dateRange.startDate,
      dateRange.endDate
    ).route,
  });

  useEffect(() => {
    const getTimestamp = (date: Date | null | string | unknown): number | undefined => {
      if (!date) return undefined;
      if (date instanceof Date) return date.getTime();
      if (typeof date === 'string') {
        const parsed = new Date(date);
        return isNaN(parsed.getTime()) ? undefined : parsed.getTime();
      }
      return undefined;
    };
    const start = getTimestamp(startDate);
    const end = getTimestamp(endDate);
    setDateRange({ startDate: start, endDate: end });
  }, [startDate, endDate]);

  useEffect(() => {
    reloadData();
  }, [dateRange]);

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const summary = reportData?.data?.summary;
  const byType = reportData?.data?.feesByType || [];
  const byDay = reportData?.data?.feesByDay || [];

  const statCards = summary
    ? [
        {
          label: "Total Fees Collected",
          value: formatCurrency(summary.totalFeesCollected),
          color: "#699269",
        },
        {
          label: "Total Transactions",
          value: summary.totalFeeTransactions.toLocaleString(),
          color: "#558B2F",
        },
        {
          label: "Active Fees",
          value: summary.activeFees.toLocaleString(),
          color: "#33691E",
        },
        {
          label: "Average Fee Amount",
          value: formatCurrency(summary.averageFeeAmount),
          color: "#1B5E20",
        },
      ]
    : [];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <Group gap="md">
            <ThemeIcon size={48} radius="md" style={{ background: "#699269" }}>
              <IconReportAnalytics size={24} color="white" />
            </ThemeIcon>
            <div>
              <Title order={2} fw={700}>
                Fee Collection Report
              </Title>
              <Text size="sm" c="dimmed">
                Detailed breakdown of all fees.
              </Text>
            </div>
          </Group>
          <Group gap="sm">
            <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => reloadData()}>
              Refresh
            </Button>
            <Button variant="light" leftSection={<IconDownload size={16} />} disabled>
              Export
            </Button>
          </Group>
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group gap="md">
            <DatePickerInput
              label="Start Date"
              placeholder="Select start date"
              value={startDate}
              onChange={(value) => setStartDate(value as unknown as Date | null)}
              leftSection={<IconCalendar size={16} />}
              clearable
            />
            <DatePickerInput
              label="End Date"
              placeholder="Select end date"
              value={endDate}
              onChange={(value) => setEndDate(value as unknown as Date | null)}
              leftSection={<IconCalendar size={16} />}
              clearable
              minDate={
                startDate
                  ? startDate instanceof Date
                    ? new Date(startDate.getTime() + 86400000)
                    : new Date(new Date(startDate as string).getTime() + 86400000)
                  : undefined
              }
            />
          </Group>
        </Paper>

        {isLoading ? (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Group justify="center">
              <Loader size="lg" />
            </Group>
          </Paper>
        ) : summary ? (
          <>
            <Grid gutter="md">
              {statCards.map((card, index) => (
                <Grid.Col key={card.label} span={{ base: 12, sm: 6, md: 3 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Paper shadow="sm" p="lg" radius="md" withBorder style={{ height: "100%" }}>
                      <Stack gap="xs">
                        <Text size="sm" c="dimmed" fw={500}>
                          {card.label}
                        </Text>
                        <Text size="xl" fw={700} style={{ color: card.color }}>
                          {card.value}
                        </Text>
                      </Stack>
                    </Paper>
                  </motion.div>
                </Grid.Col>
              ))}
            </Grid>

            {byType.length > 0 && (
              <Paper shadow="sm" p="lg" radius="md" withBorder>
                <Title order={4} mb="md">
                  Fees by Type
                </Title>
                <Table.ScrollContainer minWidth={300}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Fee Name</Table.Th>
                        <Table.Th>Type</Table.Th>
                        <Table.Th>Rate</Table.Th>
                        <Table.Th>Transactions</Table.Th>
                        <Table.Th style={{ textAlign: "right" }}>Total Collected</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {byType.map((item, index) => (
                        <Table.Tr key={index}>
                          <Table.Td>
                            <Text fw={500}>{item.feeName}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" tt="capitalize">
                              {item.feeType}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {item.feeType === "percentage"
                                ? `${item.feeValue}%`
                                : formatCurrency(item.feeValue)}
                            </Text>
                          </Table.Td>
                          <Table.Td>{item.transactionCount}</Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            <Text fw={600}>{formatCurrency(item.totalCollected)}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Paper>
            )}

            {byDay.length > 0 && (
              <Paper shadow="sm" p="lg" radius="md" withBorder>
                <Title order={4} mb="md">
                  Daily Fee Collection
                </Title>
                <Table.ScrollContainer minWidth={300}>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Date</Table.Th>
                        <Table.Th>Count</Table.Th>
                        <Table.Th style={{ textAlign: "right" }}>Amount</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {byDay.map((item, index) => (
                        <Table.Tr key={index}>
                          <Table.Td>
                            <Text fw={500}>{new Date(item._id).toLocaleDateString()}</Text>
                          </Table.Td>
                          <Table.Td>{item.count}</Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            <Text fw={600}>{formatCurrency(item.totalAmount)}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Paper>
            )}
          </>
        ) : (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Text c="dimmed" ta="center">
              No data available
            </Text>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

